// helpers/apiError.js
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_LORAX_API_BASE_URL;

export async function fetchWithAuth(
  description,
  url,
  options = {},
  { withAuth = true, credentialsMode } = {},
) {
  const finalUrl = buildFinalUrl(url);

  let token = withAuth ? localStorage.getItem('accessToken') : null;

  const headers = options.headers ? { ...options.headers } : {};

  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    delete headers['Authorization'];
  }

  headers['Accept-Language'] = (window.i18n && window.i18n.language) || 'fr';

  const finalOptions = {
    ...options,
    headers,
    credentials: credentialsMode ?? (withAuth ? 'include' : 'omit'),
    signal: options.signal ?? undefined,
  };

  const response = await fetch(finalUrl, finalOptions);

  if (response.status === 204) return { response, payload: null };

  let payload;
  try {
    payload = await response.json();
  } catch {
    throw new Error(`${description}: invalid JSON response`);
  }

  const data = payload?.data ? payload.data : payload;

  if (!response.ok) {
    let msg = data?.detail || data?.message || `${description} failed`;
    let errors = [];

    if (Array.isArray(data?.errors)) {
      errors = data.errors.map((e) =>
        typeof e === 'string' ? { message: e } : e,
      );
      if (!data?.detail && !data?.message && errors.length) {
        msg = errors[0]?.message || msg;
      }
    }

    const err = new Error(msg);
    err.status = response.status;
    err.payload = data;
    if (errors.length) err.errors = errors;
    throw err;
  }

  return { response, data };
}

export function handleApiError(error, contextMessage) {
  if (Array.isArray(error.errors)) {
    error.errors.forEach((e) =>
      toast.error(`${contextMessage}: ${e.message || e}`),
    );
  } else {
    toast.error(`${contextMessage}: ${error.message || 'Unknown error'}`);
  }
}

export async function fetchWithAuthBinary(description, url, options = {}) {
  let token = localStorage.getItem('accessToken');
  options.headers = options.headers || {};
  options.headers['Authorization'] = `Bearer ${token}`;
  options.credentials = 'include';

  const doFetch = async () => {
    const response = await fetch(url, options);
    if (!response.ok) {
      const error = new Error(`${description} failed`);
      error.status = response.status;
      throw error;
    }
    return response;
  };

  let response = await doFetch();

  if (response.status === 401) {
    if (!token) {
      throw new Error('Not authentified');
    }
    localStorage.setItem('accessToken', token);
    options.headers['Authorization'] = `Bearer ${token}`;
    response = await doFetch();
  }

  return response;
}

function buildFinalUrl(url) {
  // URL absolute : do not modify
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  // Security : relative URLs MUST start with /
  if (!url.startsWith('/')) {
    throw new Error(
      `fetchWithAuth: relative URL must start with "/" (received "${url}")`,
    );
  }

  // Normalization : avoid double slash
  return `${API_BASE_URL.replace(/\/$/, '')}${url}`;
}
